import { observer } from 'mobx-react';
import React, { useState, useEffect, useCallback } from 'react';
import { Heading, Link, Flex } from 'component-library';
import { makeStyles } from '@material-ui/core';
import ResourceCard from './ResourceCard';
import { useCompany, useDashboardStores } from 'stores/useStores';
import { ModuleStore } from '../../../../products/dashboard/stores';
import ResourceHelpBar from './ResourceHelpBar';
import { SUPPORT_EMAIL_ADDRESS } from 'lib/constants';

export interface ResourcesContainerProps {
  title: string;
  dataTestId?: string;
}

const MAX_NUM_CHILDREN = 3;

const useStyles = makeStyles({
  root: {
    width: 'inherit',
  },
  title: {
    lineHeight: '36px',
  },
  helpCenterLink: {
    lineHeight: '24px',
  },
});

/**
 * Builds a single-line mailto href for contacting support, with a prefilled
 * subject and body carrying the company triage details. Subject and body are
 * built as plain strings first, then individually encoded so that newlines
 * and special characters in the company name (&, =, unicode, etc.) can't
 * corrupt the URL. If company data isn't available, returns a bare address
 * so the link never renders `undefined` in the draft.
 */
export const buildSupportMailtoHref = (
  companyName?: string,
  companyId?: number | string,
): string => {
  const address = `mailto:${SUPPORT_EMAIL_ADDRESS}`;

  if (!companyName || companyId === undefined || companyId === null) {
    return address;
  }

  const subject = 'I need help!';
  const body = [
    '',
    '',
    '',
    '---------------------------------------------------------',
    'Please do not delete beneath this line:',
    `Company Name: ${companyName}`,
    `Company ID: ${companyId}`,
  ].join('\n');

  return `${address}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
};

const ResourcesContainer: React.FC<ResourcesContainerProps> = observer(
  ({ title, dataTestId = 'resources-container' }) => {
    const classes = useStyles();
    const { company } = useCompany();
    const { modules } = useDashboardStores();
    const [resourceCards, setResourceCards] = useState<JSX.Element[]>([]);

    /**
     * Renders up to MAX_NUM_CHILDREN resource cards, ordered by priority.
     */
    const getResourceCards = () => {
      const resources = modules.resourceCards.slice().sort((a, b) => {
        return a.priority - b.priority;
      });
      const resourcesToRender = resources.slice(0, MAX_NUM_CHILDREN);
      const backupGradients = ModuleStore.getRandomGradients(
        resourcesToRender.length,
      );
      return resourcesToRender.map((resource, index) => (
        <ResourceCard
          key={resource.title}
          priority={resource.priority}
          link={resource.resourceUrl}
          image={resource.imageUrl || backupGradients[index]}
          category={resource.category}
          title={resource.title}
          subtext={resource.subtext}
          dataTestId={`resource-card-${index}`}
        />
      ));
    };

    useEffect(() => {
      modules.initializeResourceCards();
      setupResourceCards();
    }, [modules.resourceCards.length]);

    const setupResourceCards = useCallback(() => {
      if (modules.resourceCards.length > 0) {
        // Storing the resource cards as state prevents the gradients from
        // changing whenever the component may be re-rendered
        setResourceCards(getResourceCards);
      }
    }, [resourceCards]);

    return (
      <Flex direction='column' className={classes.root}>
        <Flex
          direction='row'
          justifyContent='space-between'
          padding={[0, 0, 16, 0]}
          alignItems='end'
        >
          <Heading
            tag='h4'
            text={title}
            variant='medium'
            className={classes.title}
            noMargin
          />
          <Link
            text='Visit the Help Center'
            href='https://help.mainstreet.com/s/topic/0TO5f000000I96yGAC/mainstreet-basics-and-general-support'
            target='_blank'
            omitIcon
            variant='medium'
            className={classes.helpCenterLink}
            size={15}
          />
        </Flex>
        <Flex
          direction='row'
          justifyContent='space-between'
          alignItems='flex-start'
          gap={24}
          dataTestId={`${dataTestId}-children-container`}
          padding={[0, 0, 34, 0]}
        >
          {resourceCards}
        </Flex>
        <ResourceHelpBar
          supportMailtoHref={buildSupportMailtoHref(company?.name, company?.id)}
        />
      </Flex>
    );
  },
);

export default ResourcesContainer;
